import type { AnalyticsFilter, AnalyticsQuery, SortDirection } from '@/shared/api';
import { longDate } from '@/shared/lib';
import { formatFilter, validateFilter } from './filters';
import { findColumn, timeSortColumn, type AnalyticsColumn, type AnalyticsObject } from './schema';

/**
 * Views of a data explorer table: named filter sets with an optional sort, like the saved views
 * of the portal lists. Each table has predefined views (below); the viewer adds their own, which
 * are kept in this browser (`tableViewsSlice`).
 */
export interface TableView {
  id: string;
  name: string;
  description?: string;
  filters: AnalyticsFilter[];
  sortBy?: string;
  sortDirection?: SortDirection;
}

export const ALL_VIEW_ID = 'all';
export const RECENT_VIEW_ID = 'last-30-days';
const RECENT_DAYS = 30;

/** Columns that date when a row was created or happened, in order of preference. */
const CREATED_COLUMNS = [
  'Created',
  'CreatedAt',
  'EventTime',
  'UsageDate',
  'UsageMonth',
  'StartDate',
  'FirstSeenAt',
  'StartedAt',
];
const TEMPORAL_KINDS = new Set(['datetime', 'datetimeoffset', 'date']);

/**
 * The column that dates a row: Stripe `Created`, product `CreatedAt`, event and usage dates.
 * Dates stored as text (several `api` tables) do not count: they cannot be compared as dates.
 */
export function createdColumn(object: AnalyticsObject): AnalyticsColumn | undefined {
  const candidates = [...CREATED_COLUMNS, timeSortColumn(object)];
  return candidates
    .map((name) => findColumn(object, name))
    .find((column) => column !== undefined && TEMPORAL_KINDS.has(column.kind));
}

/**
 * A creation date stored as text (api.TeamMembers `CreatedAt`, api.Subscriptions `ActivatedAt`, …)
 * on a table without a real date column: it explains why the table has no date counts.
 */
export function textDateColumn(object: AnalyticsObject): AnalyticsColumn | undefined {
  if (createdColumn(object)) return undefined;
  return [...CREATED_COLUMNS, 'ActivatedAt', 'EditedAt']
    .map((name) => findColumn(object, name))
    .find((column) => column?.kind === 'text');
}

/** Midnight UTC `days` before `today`, as a filter value for the column's type. */
export function daysAgoValue(column: AnalyticsColumn, today: Date, days: number): string {
  const day = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - days),
  )
    .toISOString()
    .slice(0, 10);
  return column.kind === 'datetimeoffset' ? `${day}T00:00:00+00:00` : day;
}

/** Rows of the column since `RECENT_DAYS` days ago. */
export function recentFilter(column: AnalyticsColumn, today: Date): AnalyticsFilter {
  return { column: column.name, operator: 'gte', value: daysAgoValue(column, today, RECENT_DAYS) };
}

// ---------- Predefined views ----------

type Preset = Omit<TableView, 'sortBy' | 'sortDirection'>;

const eq = (column: string, value: string): AnalyticsFilter => ({ column, operator: 'eq', value });

/** One view per value of a column, e.g. Status = paid → "Paid". */
const byValue =
  (column: string) =>
  (value: string, name: string, description?: string): Preset => ({
    id: value.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name,
    filters: [eq(column, value)],
    ...(description ? { description } : {}),
  });

const flag = (column: string, name: string, description?: string, value = 'true'): Preset => ({
  id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  name,
  filters: [eq(column, value)],
  ...(description ? { description } : {}),
});

const prefix = (column: string, value: string, name: string): Preset => ({
  id: value.replace(/[^a-z0-9]+/gi, '-').replace(/-$/, ''),
  name,
  description: `${column} starts with "${value}"`,
  filters: [{ column, operator: 'startsWith', value }],
});

const status = byValue('Status');
const type = byValue('Type');
const platform = byValue('Platform');
const PLATFORMS = [
  platform('Web', 'Web'),
  platform('Android', 'Android'),
  platform('iOS', 'iOS'),
  platform('Embedded', 'Embedded', 'Apps embedded in other websites'),
];
const WEB_CLIENTS = [
  platform('Web', 'Web'),
  platform('Embedded', 'Embedded', 'Apps embedded in other websites'),
  platform('Teams', 'Teams', 'Microsoft Teams'),
];
const ACTIVE = flag('Active', 'Active');
const PREVIEW_PLATFORMS = [platform('Web', 'Web'), platform('Embedded', 'Embedded')];
const licenseStatus = byValue('SubscriptionStatus');

/**
 * Views on the Open as App links the Stripe sync sets (OaaUserId, OaaSubscriptionId); offered on
 * every table that has the column.
 */
const OAA_LINKS: Preset[] = [
  {
    id: 'oaa-user',
    name: 'Linked to an OAA user',
    description: 'OAA user ID is set (dbo.Users)',
    filters: [{ column: 'OaaUserId', operator: 'isNotNull' }],
  },
  {
    id: 'no-oaa-user',
    name: 'No OAA user',
    description: 'OAA user ID is empty',
    filters: [{ column: 'OaaUserId', operator: 'isNull' }],
  },
  {
    id: 'oaa-license',
    name: 'Linked to an OAA license',
    description: 'OAA subscription ID is set (dbo.Subscriptions)',
    filters: [{ column: 'OaaSubscriptionId', operator: 'isNotNull' }],
  },
];

/**
 * Filter sets per table (`{schema}/{slug}`), on the values the tables hold (checked against
 * oaa-sql-analytics on 15 Oct 2026). A view whose column no longer exists is left out.
 */
const PRESETS: Record<string, Preset[]> = {
  'stripe/invoices': [
    status('paid', 'Paid'),
    status('open', 'Open', 'Finalized and waiting for payment'),
    status('draft', 'Draft', 'Not finalized yet'),
    status('uncollectible', 'Uncollectible', 'Marked as unlikely to be paid'),
    status('void', 'Void'),
  ],
  'stripe/charges': [
    status('succeeded', 'Succeeded'),
    status('failed', 'Failed'),
    flag('Refunded', 'Refunded', 'Fully refunded charges'),
    flag('Disputed', 'Disputed'),
  ],
  'stripe/subscriptions': [
    status('active', 'Active'),
    {
      id: 'cancelling',
      name: 'Cancelling',
      description: 'Active, set to cancel at the end of the period',
      filters: [eq('Status', 'active'), eq('CancelAtPeriodEnd', 'true')],
    },
    status('past_due', 'Past due', 'The latest invoice could not be paid'),
    status('canceled', 'Canceled'),
    status('incomplete_expired', 'Incomplete expired', 'The first payment never completed'),
  ],
  'stripe/payment-intents': [
    status('succeeded', 'Succeeded'),
    status('requires_payment_method', 'Requires payment method', 'Failed or not attempted yet'),
    status('canceled', 'Canceled'),
  ],
  'stripe/customers': [flag('Delinquent', 'Delinquent', 'The latest invoice payment failed')],
  'stripe/disputes': [status('lost', 'Lost'), status('won', 'Won')],
  'stripe/prices': [
    ACTIVE,
    {
      id: 'yearly',
      name: 'Yearly',
      description: 'Active prices billed every year',
      filters: [eq('Active', 'true'), eq('RecurringInterval', 'year')],
    },
    {
      id: 'monthly',
      name: 'Monthly',
      description: 'Active prices billed every month',
      filters: [eq('Active', 'true'), eq('RecurringInterval', 'month')],
    },
    type('one_time', 'One-time'),
  ],
  'stripe/products': [ACTIVE],
  'stripe/coupons': [flag('Valid', 'Valid', 'Can still be redeemed')],
  'stripe/tax-rates': [ACTIVE],
  'stripe/balance-transactions': [
    type('charge', 'Charges'),
    type('stripe_fee', 'Stripe fees'),
    type('payout', 'Payouts'),
    type('refund', 'Refunds'),
    status('pending', 'Pending', 'Not available for payout yet'),
  ],
  'stripe/events': [
    prefix('Type', 'invoice.', 'Invoice events'),
    prefix('Type', 'customer.subscription.', 'Subscription events'),
    prefix('Type', 'payment_intent.', 'Payment events'),
    prefix('Type', 'charge.', 'Charge events'),
  ],
  'stripe/invoice-lines': [
    type('subscription', 'Subscription lines'),
    type('invoiceitem', 'Invoice items', 'One-off items and prorations'),
    flag('Proration', 'Prorations', 'Charges or credits for plan changes within a period'),
  ],
  'stripe/objects': [
    byValue('ObjectType')('checkout.session', 'Checkout sessions'),
    byValue('ObjectType')('payment_method', 'Payment methods'),
    byValue('ObjectType')('invoiceitem', 'Invoice items'),
    byValue('ObjectType')('setup_intent', 'Setup intents'),
    byValue('ObjectType')('promotion_code', 'Promotion codes'),
    byValue('ObjectType')('credit_note', 'Credit notes'),
  ],
  'api/team-members': [
    byValue('Role')('Owner', 'Owners'),
    byValue('Role')('Admin', 'Admins'),
    byValue('Role')('Member', 'Members'),
    byValue('Role')('Creator', 'Creators'),
  ],
  'api/app-invitations': [byValue('Role')('Admin', 'Admins'), byValue('Role')('User', 'Users')],
  'api/apps': [
    flag('IsHidden', 'Visible', 'Apps not hidden by their team', 'false'),
    flag('IsHidden', 'Hidden'),
    flag('HasWebhooks', 'With webhooks'),
    flag('IsFromSampleFile', 'From a sample file'),
  ],
  'api/creators': [
    flag('EmailIsConfirmed', 'Email confirmed'),
    flag('EmailIsConfirmed', 'Email not confirmed', undefined, 'false'),
  ],
  'dbo/app-usage': [
    byValue('OsName')('Android', 'Android'),
    byValue('OsName')('iOS', 'iOS'),
    byValue('OsName')('Web', 'Web'),
    byValue('OsName')('embedded', 'Embedded', 'Apps embedded in other websites'),
  ],
  'dbo/web-client-usage-total': WEB_CLIENTS,
  'dbo/web-client-usage-30-days': WEB_CLIENTS,
  'dbo/users': [
    flag('EmailIsConfirmed', 'Email confirmed'),
    flag('EmailIsConfirmed', 'Email not confirmed', undefined, 'false'),
    flag('IsTest', 'Test accounts'),
  ],
  'dbo/subscriptions': [
    licenseStatus('active', 'Active'),
    licenseStatus('canceled', 'Canceled'),
    licenseStatus('deactivated', 'Deactivated'),
    licenseStatus('disabled', 'Disabled'),
    licenseStatus('archived', 'Archived'),
    licenseStatus('deleted', 'Deleted'),
    byValue('PaymentInterval')('Month', 'Billed monthly'),
    byValue('PaymentInterval')('Year', 'Billed yearly'),
    {
      id: 'stripe',
      name: 'With a Stripe subscription',
      description: 'Stripe subscription ID is set',
      filters: [{ column: 'StripeSubscriptionId', operator: 'isNotNull' }],
    },
  ],
  'dbo/team-users': [byValue('Role')('Admin', 'Admins'), byValue('Role')('Creator', 'Creators')],
  'dbo/resources': [
    byValue('Discriminator')('App', 'Apps'),
    byValue('Discriminator')('Hub', 'Hubs'),
    flag('IsHidden', 'Visible', 'Resources not hidden by their team', 'false'),
    flag('IsHidden', 'Hidden'),
    flag('IsTemplate', 'Templates'),
  ],
  'dbo/subscription-plans': [flag('IsCustom', 'Custom plans')],
  'bi/app-event': PLATFORMS,
  'bi/app-usage-daily': PLATFORMS,
  'bi/app-usage-monthly': PLATFORMS,
  'bi/preview-interaction-daily': PREVIEW_PLATFORMS,
  'bi/vw-app-event': PLATFORMS,
  'bi/vw-app-usage-daily': PLATFORMS,
  'bi/vw-app-usage-monthly': PLATFORMS,
  'bi/vw-preview-interaction-daily': PREVIEW_PLATFORMS,
  'etl/run-log': [
    byValue('Job')('events', 'Event loads', 'One source, 10-minute window'),
    byValue('Job')('preview', 'Preview loads'),
    byValue('Job')('usage', 'Usage rebuilds', 'bi.AppUsageDaily for one day'),
    {
      id: 'not-ok',
      name: 'Not ok',
      description: 'Runs that did not end with status ok',
      filters: [{ column: 'Status', operator: 'ne', value: 'ok' }],
    },
  ],
};

/**
 * Predefined views of a table: "All records" first (the default), then "Last 30 days" when rows
 * are dated, then the table's own filter sets. Dated views open newest first.
 */
export function predefinedTableViews(
  object: AnalyticsObject,
  today: Date,
): [TableView, ...TableView[]] {
  const created = createdColumn(object);
  const newestFirst = created ? { sortBy: created.name, sortDirection: 'desc' as const } : {};
  const views: [TableView, ...TableView[]] = [
    { id: ALL_VIEW_ID, name: 'All records', filters: [] },
  ];
  if (created) {
    const filter = recentFilter(created, today);
    views.push({
      id: RECENT_VIEW_ID,
      name: `Last ${RECENT_DAYS} days`,
      description: `${created.label} on or after ${longDate(filter.value)}`,
      filters: [filter],
      ...newestFirst,
    });
  }
  for (const preset of [...(PRESETS[object.key] ?? []), ...OAA_LINKS]) {
    if (preset.filters.some((filter) => validateFilter(object, filter) !== null)) continue;
    views.push({ ...preset, ...newestFirst });
  }
  return views;
}

// ---------- Comparing views with the current list ----------

const filterKey = (filter: AnalyticsFilter) =>
  formatFilter({ ...filter, column: filter.column.toLowerCase() });

/** Same filters (in any order) and the same sort. */
export function sameQuery(a: AnalyticsQuery, b: AnalyticsQuery): boolean {
  const filtersA = a.filters.map(filterKey).sort();
  const filtersB = b.filters.map(filterKey).sort();
  return (
    filtersA.length === filtersB.length &&
    filtersA.every((key, index) => key === filtersB[index]) &&
    (a.sortBy ?? '').toLowerCase() === (b.sortBy ?? '').toLowerCase() &&
    (a.sortBy === undefined || a.sortDirection === b.sortDirection)
  );
}

/**
 * Splits the list's filters into the view's own (with any others from the link) and the extra
 * condition set in the filter dropdowns: the last filter the view does not have.
 */
export function splitExtraFilter(
  viewFilters: AnalyticsFilter[],
  filters: AnalyticsFilter[],
): { base: AnalyticsFilter[]; extra: AnalyticsFilter | undefined } {
  const unmatched = viewFilters.map(filterKey);
  let extraIndex = -1;
  filters.forEach((filter, index) => {
    const match = unmatched.indexOf(filterKey(filter));
    if (match === -1) extraIndex = index;
    else unmatched.splice(match, 1);
  });
  return {
    base: filters.filter((_, index) => index !== extraIndex),
    extra: filters[extraIndex],
  };
}
